import { get } from "lodash";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";

import { isStepReachable } from "./multistepNavigation";

export type Step<TFieldValues extends FieldValues = FieldValues> = {
    label: string;
    validate: Path<TFieldValues>[];
    optional?: boolean;
    enabled?: boolean;
    criticalFields?: string[]; // Fields required even in draft mode for navigation
};

type UseMultistepProps<TFieldValues extends FieldValues = FieldValues, TContext = any> = {
    steps: Array<Step<TFieldValues>>;
    form: UseFormReturn<TFieldValues, TContext>;
    defaultStep?: number;
    defaultMaxVisited?: number;
    validateOnMount?: boolean;
    draftMode?: boolean;
};

export function useMultistep<TFieldValues extends FieldValues = FieldValues, TContext = any>(
    {
        steps,
        form,
        defaultMaxVisited = 0,
        defaultStep = 0,
        validateOnMount = true,
        draftMode = false,
    }: UseMultistepProps<TFieldValues, TContext>,
    deps: any[] = [],
) {
    const {
        formState: { errors },
        trigger,
        register,
        watch,
    } = form;

    const formValues = watch();

    const hasError = useCallback(
        (name: string) => {
            return !!get(errors, name);
        },
        [errors],
    );

    const hasValue = useCallback(
        (name: string) => {
            const value = get(formValues, name);

            // Handle common empty cases in one check
            if (value === undefined || value === null || value === "") {
                return false;
            }

            // Empty arrays count as filled: an optional list must not block its step (SOLV3-3064).
            if (Array.isArray(value)) {
                return true;
            }

            // For objects, check if they have properties
            if (typeof value === "object") {
                return Object.keys(value).length > 0;
            }

            // For all other types, consider them as having a value
            return true;
        },
        [formValues],
    );

    // Automatically determine if we should enable step auto-detection
    // Use validateOnMount as indicator: creation forms (false) vs edit forms (true)
    const autoDetectValidatedSteps = useMemo(() => {
        if (!validateOnMount) {
            return false;
        }

        // If validating on mount, check if we have existing data (edit scenario)
        const hasExistingData = steps.some(step => {
            const { validate = [] } = step;
            return validate.some(field => hasValue(field));
        });

        return hasExistingData;
    }, [validateOnMount, steps, hasValue]);

    // Auto-detect valid steps if enabled
    const calculatedMaxVisited = useMemo(() => {
        if (!autoDetectValidatedSteps) {
            return defaultMaxVisited;
        }

        let maxValidStep = -1;

        // Check each step to find the highest valid one
        for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
            const step = steps[stepIndex];
            const { validate = [], optional = false } = step;

            // If step has no validation rules, consider it valid
            if (validate.length === 0) {
                maxValidStep = stepIndex;
                continue;
            }

            // Check if all validation fields are valid
            const isStepValid = validate.every(field => {
                // If the field has an error, it's not valid
                if (hasError(field)) {
                    return false;
                }

                // If the step is optional, we don't need to check if fields have values
                if (optional) {
                    return true;
                }

                // For all steps, fields must have values to be considered valid
                return hasValue(field);
            });

            if (isStepValid) {
                maxValidStep = stepIndex;
            } else {
                // Stop at first invalid step (don't mark future steps as valid)
                break;
            }
        }

        return Math.max(0, maxValidStep, defaultMaxVisited);
    }, [autoDetectValidatedSteps, defaultMaxVisited, steps, hasError, hasValue, draftMode]);

    const [state, setState] = useState({
        current: defaultStep,
        maxVisited: Math.max(0, defaultStep, calculatedMaxVisited),
    });

    // DefaultStep can be changed after initial render
    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            current: defaultStep,
        }));
    }, [defaultStep]);

    const { current: currentStep, maxVisited } = state;

    // Update maxVisited when calculatedMaxVisited changes (for auto-detection)
    useEffect(() => {
        if (autoDetectValidatedSteps) {
            setState(prevState => ({
                ...prevState,
                maxVisited: Math.max(prevState.maxVisited, calculatedMaxVisited),
            }));
        }
    }, [calculatedMaxVisited, autoDetectValidatedSteps]);

    steps.forEach(({ validate = [] }) => {
        validate.forEach(name => register(name as Path<TFieldValues>));
    });

    useEffect(() => {
        if (validateOnMount) {
            trigger();
        }
    }, [validateOnMount]);

    const stepsWithState = useMemo(() => {
        return steps
            .map(({ validate = [], criticalFields = [], ...step }, index) => {
                const stepState = {
                    valid:
                        validate.length === 0
                            ? true
                            : validate.every(field => {
                                  // If the field has an error, it's not valid
                                  if (hasError(field)) {
                                      return false;
                                  }

                                  // If the step is optional, we don't need to check if fields have values
                                  if (step.optional) {
                                      return true;
                                  }

                                  // For all steps (draft or normal mode), fields must have values to be considered valid
                                  // In draft mode, the difference is in navigation (reachability), not in validity display
                                  return hasValue(field);
                              }),
                    optional: step.optional ?? false,
                    visited: draftMode
                        ? // In draft mode, only mark as visited if step is complete or has errors
                          index <= maxVisited &&
                          // Step is complete (all validation fields have values and no errors)
                          (validate.length === 0 ||
                              validate.every(field => hasValue(field) && !hasError(field)) ||
                              // Or step has validation errors
                              validate.some(field => hasError(field)))
                        : // Normal mode use standard visited logic
                          maxVisited >= index,
                    enabled: step.enabled ?? true,
                };

                return {
                    ...step,
                    validate,
                    criticalFields,
                    ...stepState,
                };
            })
            .map((stepWithState, stepIndex, steps) => {
                // For navigation purposes, use different logic in draft mode
                let reachable;
                if (draftMode) {
                    // In draft mode, check if critical fields are satisfied for previous steps
                    const previousStepsValid = steps.slice(0, stepIndex).every(prevStep => {
                        const { criticalFields = [] } = prevStep;
                        return (
                            criticalFields.length === 0 ||
                            criticalFields.every(field => {
                                return !hasError(field) && hasValue(field);
                            })
                        );
                    });
                    reachable = previousStepsValid;
                } else {
                    // Normal mode: use standard reachability logic
                    reachable = isStepReachable({ step: stepIndex, stepsStates: steps, currentStep });
                }

                return {
                    ...stepWithState,
                    reachable,
                };
            });
    }, [steps, hasError, hasValue, maxVisited, currentStep, validateOnMount, draftMode, ...deps]);

    const canJump = useCallback(
        (step: number) => step >= 0 && step < stepsWithState.length && stepsWithState[step].reachable,
        [stepsWithState],
    );

    const jump = useCallback(
        (step: number) => {
            if (canJump(step)) {
                setState(state => ({ maxVisited: Math.max(state.maxVisited, step), current: step }));
            }
        },
        [canJump],
    );

    /**
     * Sends the user back to the first step holding an error, so a rejected submission
     * shows what to fix instead of failing silently where nothing is wrong.
     *
     * Call it once validation has run: it reads the errors, it does not raise them.
     * Returns the step it moved to, or -1 when it stayed put — `jump` refuses a step
     * that is not reachable yet, and the caller is then the one owing feedback.
     */
    const jumpToFirstInvalidStep = useCallback(() => {
        const firstInvalidStep = steps.findIndex(step =>
            step.validate.some(field => Boolean(form.getFieldState(field).error)),
        );

        if (firstInvalidStep < 0 || firstInvalidStep === currentStep || !canJump(firstInvalidStep)) {
            return -1;
        }

        jump(firstInvalidStep);

        return firstInvalidStep;
    }, [steps, form, currentStep, canJump, jump]);

    const next = useMemo(() => {
        return stepsWithState.findIndex((step, index) => index > currentStep && step.reachable);
    }, [stepsWithState, currentStep]);

    const prev = useMemo(() => {
        // Find last index manually since findLastIndex might not be available in older TS lib
        for (let i = currentStep - 1; i >= 0; i--) {
            if (stepsWithState[i] && stepsWithState[i].reachable) {
                return i;
            }
        }
        return -1;
    }, [stepsWithState, currentStep]);

    return useMemo(
        () => ({
            steps: stepsWithState,
            stepIndex: currentStep,
            step: stepsWithState[currentStep],
            maxStepVisited: maxVisited,
            jump,
            jumpToFirstInvalidStep,
            next,
            prev,
            isFirstStep: currentStep === 0,
            isLastStep: currentStep === stepsWithState.length - 1,
        }),
        [jump, jumpToFirstInvalidStep, next, prev, maxVisited, currentStep, stepsWithState],
    );
}

export type UseMultistepReturn<TFieldValues extends FieldValues = FieldValues, TContext = any> = ReturnType<
    typeof useMultistep<TFieldValues, TContext>
>;

const context = createContext<UseMultistepReturn | null>(null);

export const useMultistepContext = () => useContext(context);

export function MultistepProvider<TFieldValues extends FieldValues = FieldValues>({
    children,
    ...props
}: { children: ReactNode } & UseMultistepReturn<TFieldValues>) {
    return <context.Provider value={props}>{children}</context.Provider>;
}

export default useMultistep;
